// Self-Healing Repair Layer · Session 19AU.
//
// Per陛下 "渐进式修复" insight: LLM-native bugs are mostly DATA quality
// issues, not code issues. Fix them at the data layer (cost ~$0.001-0.01)
// instead of regenerating the whole IngestPackage (cost $0.5-3 + lost
// progress).
//
// Three trigger modes (MVP only ships user-triggered; auto + suggested
// are scaffolded but gated behind future work):
//   - silent_auto    — lint detects structural fail → repair without UX
//   - toast_suggested — lint detects borderline → toast offers repair
//   - user_triggered — user clicks "AI 修复" on a flagged item
//
// Storage philosophy (mirrors §2.3 EditableItem · 19AP):
//   IngestPackage stays pure (AI original output). Repairs live in
//   CourseProgress.repairs[itemId], same way ItemOverride lives in
//   itemOverrides. View renders via resolveItem(pkg.X, progress.repairs).
//
// Why a separate store from itemOverrides:
//   ItemOverride is FIELD-level (e.g. mcq.explanation only). Repair is
//   ITEM-level (entire mcq replaced atomically). Different cardinality,
//   different lifecycle (repairs have history + budget), keep separate.
import { z } from 'zod';
/**
 * RepairAttemptReason — discriminated union of "why we tried to repair".
 * Drives prompt construction (lint message vs user note as primary signal).
 */
export const RepairAttemptReasonSchema = z.discriminatedUnion('mode', [
    z.object({
        mode: z.literal('lint_auto'),
        /** Lint rule id (e.g. 'mcq.distractor.balance'). */
        lintRule: z.string(),
        severity: z.enum(['borderline', 'fail']),
        /** Human-readable description of what the lint caught. */
        detail: z.string(),
    }),
    z.object({
        mode: z.literal('user_report'),
        /** Free-form user message (from FlagButton note OR explicit repair dialog). */
        userMessage: z.string().min(1).max(2000),
    }),
]);
/**
 * RepairAttemptResult — outcome of a single repair LLM call. Discriminated
 * union by status so consumers can pattern-match without optional juggling.
 */
export const RepairAttemptResultSchema = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('success'),
        /** The repaired item JSON. Schema-validated against the original kind
         *  before this status is recorded. */
        output: z.unknown(),
        reasoning: z.string().optional(),
    }),
    z.object({
        status: z.literal('schema_fail'),
        schemaErrors: z.array(z.string()),
    }),
    z.object({
        status: z.literal('lint_fail'),
        lintIssues: z.array(z.string()),
        /** Output is still preserved — user may accept it anyway. */
        output: z.unknown(),
    }),
    z.object({
        status: z.literal('provider_error'),
        message: z.string(),
    }),
    z.object({
        status: z.literal('budget_exhausted'),
        cap: z.number(),
        used: z.number(),
    }),
]);
/**
 * RepairFailureKind — taxonomy used by Layer-3 retry hint formatter.
 * Derived from RepairAttemptResult.status + the guard report; persisted on
 * RepairAttempt so the operator dashboard can group-by it.
 *
 * `forced_revert` — the LLM mutated a `repair_locked` field; server reverted.
 *                   Distinct from schema_violation: the output IS schema-valid,
 *                   the LLM just disobeyed the locked-field rule.
 * `no_improvement` — accepted previously, user re-flagged with same complaint.
 *                    Reserved; surfaces only once the dashboard wires it.
 */
export const RepairFailureKindSchema = z.enum([
    'schema_violation',
    'lint_critical',
    'forced_revert',
    'no_improvement',
]);
/**
 * GuardReport — output of `applyMutabilityGuard` (worker side, packages/ingest).
 * Persisted on each attempt for forensic / prompt-tuning analysis.
 *
 * Per REPAIR_CONTEXT_ARCHITECTURE.md §5.6: the guard report is the primary
 * observability signal for "is the repair prompt actually disciplining the
 * LLM?" — group-by `(kind, forcedReverts.field)` over attempts surfaces
 * which prompts need stronger locked-field enforcement.
 */
export const GuardReportSchema = z.object({
    /** Locked fields the LLM tried to mutate; server overwrote with original. */
    forcedReverts: z.array(z.object({
        field: z.string(),
        /** JSON-stringified to keep the schema simple; rendering decides display. */
        llmValue: z.unknown(),
        restoredTo: z.unknown(),
    })).default([]),
    /** Aware fields the LLM updated — diff recorded but not reverted. */
    awareDiffs: z.array(z.object({
        field: z.string(),
        before: z.unknown(),
        after: z.unknown(),
    })).default([]),
    /** Excluded fields the LLM emitted (theoretically impossible — prompt strips
     *  them) — surfaced for paranoia + forensic value. Server strips before
     *  persisting, but logs here. */
    excludedEmitted: z.array(z.string()).default([]),
}).passthrough();
/** RepairAttempt — one round-trip to the LLM (or rejected before call). */
export const RepairAttemptSchema = z.object({
    attemptedAt: z.string().datetime(),
    reason: RepairAttemptReasonSchema,
    result: RepairAttemptResultSchema,
    /** Cost incurred even on failed validation — provider was called. */
    costRmb: z.number().nonnegative(),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    durationMs: z.number().int().nonnegative(),
    /** Provider used for this attempt — useful for upgrade-on-repair strategies. */
    providerName: z.string().optional(),
    /** Was this attempt's output ACCEPTED by the user (or auto-accepted)? */
    acceptedByUser: z.boolean(),
    /** 19AV Phase B: present when result.status === 'success' and a guard pass
     *  ran. Absent on schema_fail / provider_error / budget_exhausted. */
    guardReport: GuardReportSchema.optional(),
    /** 19AV: derived from result.status + guardReport (set by handler so the
     *  operator dashboard doesn't have to recompute). */
    failureKind: RepairFailureKindSchema.optional(),
    /** 19AV Layer-3 cache key: 8-char sha256 prefix of the system prompt used
     *  for this attempt. Lets the dashboard verify byte-stability across calls. */
    promptVersion: z.string().optional(),
});
/**
 * RepairRecord — per-item repair state. Keyed by `${itemKind}:${itemId}` in
 * CourseProgress.repairs to avoid id collisions across kinds.
 *
 * Lifecycle:
 *   1. First repair attempt → record created with [attempt1]
 *   2. User accepts → current = attempt1.output, acceptedByUser=true
 *   3. Subsequent repair → attempt2 appended; current updates if accepted
 *   4. User reverts → current set to null (renders pkg AI original again)
 */
export const RepairRecordSchema = z.object({
    itemKind: z.enum([
        'mcq', 'tf', 'flashcard', 'essay', 'glossary', 'mindmap',
        'lecture_note', 'proof_drill', 'free_response', 'reading_passage',
    ]),
    itemId: z.string(),
    /** Current accepted version. null = no version accepted yet (or reverted to AI original). */
    current: z.unknown().nullable(),
    /** Append-only attempt history. */
    attempts: z.array(RepairAttemptSchema),
    /** Cumulative RMB spent on this item (sums attempts.costRmb). Cap-checked
     *  against PER_ITEM_REPAIR_CAP_USD before each new attempt. */
    budgetUsedRmb: z.number().nonnegative(),
    /** ISO datetime of latest attempt. Sort key for the operator dashboard. */
    updatedAt: z.string().datetime(),
});
/** Canonical key used in CourseProgress.repairs map. */
export function repairKey(itemKind, itemId) {
    return `${itemKind}:${itemId}`;
}
// ─── Budget caps ────────────────────────────────────────────────────────────
// Single source of truth for budget thresholds. Worker enforces per-isolate
// (existing S7 budget); client enforces per-item + per-course before sending
// the request, then trusts server response.
/** Per-item hard cap (RMB). After this an item is "stuck" — manual edit only. */
export const PER_ITEM_REPAIR_CAP_RMB = 0.10;
/** Per-course soft cap (RMB). After this auto-repairs disabled but user can override. */
export const PER_COURSE_REPAIR_CAP_RMB = 0.50;
/** Predicate — can this item still attempt repair under the budget rules? */
export function canRepairItem(record) {
    if (!record)
        return true;
    return record.budgetUsedRmb < PER_ITEM_REPAIR_CAP_RMB;
}
/** Predicate — total course-wide repair spend under the cap. */
export function canRepairInCourse(allRepairs) {
    let total = 0;
    for (const r of Object.values(allRepairs))
        total += r.budgetUsedRmb;
    return total < PER_COURSE_REPAIR_CAP_RMB;
}
/** Sum of repair RMB across all items in this course. */
export function courseRepairTotalRmb(allRepairs) {
    let total = 0;
    for (const r of Object.values(allRepairs))
        total += r.budgetUsedRmb;
    return total;
}
//# sourceMappingURL=repair.js.map